import React from "react";
import {
  Users,
  BarChart3,
  TrendingDown,
  IndianRupee,
  Award,
  Target,
  ArrowDown,
  DollarSign,
} from "lucide-react";

const mockData = [
  {
    level: "1",
    income: "1Rs ($0.012) from all directs",
    color: "from-green-500 to-green-400",
    icon: "💰",
  },
  {
    level: "2 to 5",
    income: "0.50 Rs ($0.0058) from every referral",
    color: "from-blue-500 to-blue-400",
    icon: "💎",
  },
  {
    level: "6 to 8",
    income: "0.25 Rs ($0.0029) from all team",
    color: "from-purple-500 to-purple-400",
    icon: "🎯",
  },
  {
    level: "9 to 15",
    income: "0.10 Rs ($0.0012) from all team",
    color: "from-orange-500 to-orange-400",
    icon: "⭐",
  },
  {
    level: "16 to 20",
    income: "0.05 Rs ($0.00058) from all team",
    color: "from-pink-500 to-pink-400",
    icon: "🏆",
  },
];

const PageEight = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-gray-900 to-purple-900 text-white relative overflow-hidden">
      {/* Background Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-40 h-40 bg-yellow-500/10 rounded-full blur-2xl"></div>
        <div className="absolute bottom-20 right-10 w-32 h-32 bg-blue-500/10 rounded-full blur-xl"></div>
        <div className="absolute top-1/2 left-1/2 w-24 h-24 bg-purple-500/10 rounded-full blur-lg"></div>
      </div>

      <div className="relative z-10 px-4 sm:px-6 lg:px-16 py-12 lg:py-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-start">
            {/* Left Side: Enhanced Content */}
            <div className="space-y-8">
              {/* Enhanced Title Banner */}
              <div className="inline-flex items-center gap-3 bg-gradient-to-r from-yellow-400 to-yellow-300 text-black font-bold text-lg sm:text-xl px-6 py-4 rounded-full shadow-lg hover:shadow-yellow-500/25 transform hover:scale-105 transition-all duration-300">
                <BarChart3 className="w-6 h-6" />
                <span>Team Free Referral Income</span>
              </div>

              {/* Income Structure Description */}
              <div className="bg-gray-800/60 rounded-xl p-6 border border-gray-600/50">
                <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                  <Target className="w-5 h-5 text-blue-400" />
                  Multi-Level Income Structure
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  Earn from your entire team network with our comprehensive
                  20-level income system. The deeper your network grows, the
                  more opportunities you have to earn.
                </p>
              </div>

              {/* Enhanced Income Table */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-gray-200 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-400" />
                  Income Breakdown by Level
                </h4>

                <div className="space-y-3">
                  {mockData.map((row, index) => (
                    <div
                      key={index}
                      className="group relative bg-gray-800/70 hover:bg-gray-700/70 rounded-xl p-2 sm:p-5 border border-gray-600/50 hover:border-gray-500/70 transition-all duration-300 cursor-pointer">
                      {/* Level Badge */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-5 h-5 sm:w-12 sm:h-12 bg-gradient-to-r ${row.color} rounded-full flex items-center justify-center text-xl shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                            <span className="text-white font-bold text-sm">
                              {index + 1}
                            </span>
                          </div>
                          <div>
                            <p className="text-white text-base">
                              Level {row.level}
                            </p>
                          </div>
                        </div>
                        <div className="text-xl">{row.icon}</div>
                      </div>

                      {/* Income Details */}
                      <div className="bg-gray-900/50 rounded-lg p-2 sm:p-4">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-300 text-xs sm:text-base">
                            Daily Income:
                          </span>
                          <span className="text-green-400 font-bold text-sm sm:text-lg">
                            {row.income}
                          </span>
                        </div>
                      </div>

                      {/* Hover Effect Arrow */}
                      <div className="absolute right-4 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <ArrowDown className="w-5 h-5 text-gray-400 rotate-[-90deg]" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Summary Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gradient-to-r from-green-600/20 to-green-500/20 rounded-xl p-4 border border-green-500/30">
                  <div className="text-center">
                    <p className="text-green-400 font-bold text-xl sm:text-2xl">
                      20
                    </p>
                    <p className="text-gray-300 text-sm">Income Levels</p>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-blue-600/20 to-blue-500/20 rounded-xl p-4 border border-blue-500/30">
                  <div className="text-center">
                    <p className="text-blue-400 font-bold text-xl sm:text-2xl">
                      ∞
                    </p>
                    <p className="text-gray-300 text-sm">Team Size</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side: Enhanced Visual */}
            <div className="flex justify-center lg:justify-end">
              <div className="relative w-full max-w-md lg:max-w-lg">
                {/* Main Visual Container */}
                <div className="relative bg-gradient-to-br from-gray-700 via-gray-600 to-gray-700 rounded-2xl shadow-2xl p-6 sm:p-8 border border-gray-600/50">
                  {/* Team Analysis Visual */}
                  <div className="text-center space-y-6">
                    <div className="relative">
                      <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mx-auto shadow-lg">
                        <Users className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
                      </div>
                      <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-400 rounded-full flex items-center justify-center">
                        <BarChart3 className="w-3 h-3 text-white" />
                      </div>
                    </div>

                    <div>
                      <h4 className="text-xl sm:text-2xl font-bold text-white mb-2">
                        Team Analysis
                      </h4>
                      <p className="text-gray-300 text-sm sm:text-base">
                        Multi-level income tracking
                      </p>
                    </div>

                    {/* Network Visualization */}
                    <div className="space-y-4">
                      <div className="flex justify-center items-center space-x-2">
                        {[1, 2, 3, 4, 5].map((level) => (
                          <div
                            key={level}
                            className="flex flex-col items-center">
                            <div
                              className={`w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r ${
                                mockData[level - 1]?.color ||
                                "from-gray-500 to-gray-400"
                              } rounded-full flex items-center justify-center text-white text-xs sm:text-sm font-bold shadow-md`}>
                              {level}
                            </div>
                            {level < 5 && (
                              <ArrowDown className="w-3 h-3 text-gray-400 mt-1" />
                            )}
                          </div>
                        ))}
                      </div>

                      <div className="bg-gray-800/50 rounded-lg p-4">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-400">
                            Total Potential:
                          </span>
                          <span className="text-green-400 font-bold">
                            4.7 Paise per referral ($ 0.00052)
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Decorative Elements */}
                  <div className="absolute -top-6 -right-6 w-12 h-12 bg-yellow-500/20 rounded-full blur-lg animate-pulse"></div>
                  <div className="absolute -bottom-4 -left-4 w-8 h-8 bg-purple-500/20 rounded-full blur-md animate-pulse delay-500"></div>
                </div>

                {/* Floating Achievement Badge */}
                <div className="absolute -top-4 -left-4 bg-gray-800 rounded-xl p-3 shadow-xl border border-gray-600/50 hidden sm:block">
                  <div className="flex items-center gap-2">
                    <Award className="w-5 h-5 text-yellow-400" />
                    <div>
                      <p className="text-white font-bold text-sm">Elite</p>
                      <p className="text-gray-400 text-xs">Network</p>
                    </div>
                  </div>
                </div>

                {/* Success Indicator */}
                <div className="absolute -bottom-6 -right-6 bg-gray-800 rounded-xl p-3 shadow-xl border border-gray-600/50 hidden sm:block">
                  <div className="flex items-center gap-2">
                    <TrendingDown className="w-5 h-5 text-green-400 rotate-180" />
                    <div>
                      <p className="text-green-400 font-bold text-sm">
                        Growing
                      </p>
                      <p className="text-gray-400 text-xs">Network</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Summary Cards */}
          <div className="mt-12 pt-8 border-t border-gray-700/50 lg:hidden">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="text-center p-6 bg-gray-800/50 rounded-xl border border-gray-600/30">
                <Award className="w-8 h-8 mx-auto text-yellow-400 mb-2" />
                <p className="text-xl font-bold text-white">20 Levels</p>
                <p className="text-gray-400 text-sm">Income Structure</p>
              </div>
              <div className="text-center p-6 bg-gray-800/50 rounded-xl border border-gray-600/30">
                <IndianRupee className="w-8 h-8 mx-auto text-green-400 mb-2" />
                <p className="text-xl font-bold text-green-400">
                  4.70 Paise ($ 0.00052)
                </p>
                <p className="text-gray-400 text-sm">Max Per Referral</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageEight;
